import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button, Form, Input, Select } from "antd";
import Loader from "../../../../components/Loader";
import {
  addUpdateFinancialInfo,
  getFinancialInfo,
} from "../../../../api/request/corporate";
import { handleApiError } from "../../../../utils/handleApiError";

const CORPORATE_SOURCE_OF_WEALTH_OTHER = 4;
const CORPORATE_SOURCE_OF_WEALTH_OPTIONS = [
  { value: 1, label: "Company Ownership / Profits" },
  { value: 2, label: "Company Loan" },
  { value: 3, label: "Property Sale" },
  { value: 4, label: "Other" },
];

const FinancialInfo = ({
  onNext,
  onPrevious,
  id,
  isSubmitted,
  userId,
  isReviewMode = false,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const sourceOfWealth = Form.useWatch("sourceOfWealth", form);

  const isDisabled = isSubmitted;
  const isFieldDisabled = isDisabled && !isReviewMode;

  const getFinancialInfoData = async () => {
    setIsLoading(true);
    try {
      const { data } = await getFinancialInfo(id);
      if (data?.success) {
        form.setFieldsValue({
          ...data?.data,
          sourceOfWealth: data?.data?.sourceOfWealth || [],
          otherSourceOfWealth: data?.data?.otherSourceOfWealth || "",
        });
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) getFinancialInfoData();
  }, [id]);

  const handleSubmit = async (values) => {
    if (isDisabled) return;
    setIsSubmitLoading(true);
    try {
      const hasOtherSource =
        Array.isArray(values.sourceOfWealth) &&
        values.sourceOfWealth.includes(CORPORATE_SOURCE_OF_WEALTH_OTHER);
      const payload = {
        ...values,
        userId,
        sourceOfWealth: values.sourceOfWealth || [],
        otherSourceOfWealth: hasOtherSource
          ? values.otherSourceOfWealth || ""
          : "",
      };

      if (id) Object.assign(payload, { id });

      const { data } = await addUpdateFinancialInfo(payload);

      if (data?.success) {
        toast.success(data?.message);
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="max-w-5xl mx-auto border border-gray-300 rounded-lg p-6 mb-6">
      <Form
        form={form}
        size="large"
        layout="vertical"
        autoComplete="off"
        className={isReviewMode ? "corporate-review-readonly" : undefined}
        disabled={isFieldDisabled}
        onKeyDown={(event) => {
          if (!isReviewMode) return;
          if (event.target.closest?.(".corporate-review-nav")) return;
          event.preventDefault();
        }}
        onFinish={handleSubmit}
        // validateTrigger={['onChange', 'onBlur', 'onSubmit']}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item
            label="Net Worth of the Company (USD)"
            name="companyNetWorth"
            rules={[
              {
                required: true,
                message: "Please enter net worth of the company",
              },
            ]}
          >
            <Input type="number" placeholder="Enter net worth" min={1} />
          </Form.Item>

          <Form.Item
            label="Approx. annual turnover (USD)"
            name="annualTurnOver"
            rules={[
              {
                required: true,
                message: "Please enter approximate annual turnover",
              },
            ]}
          >
            <Input type="number" placeholder="Enter annual turnover" min={1} />
          </Form.Item>

          <Form.Item
            label="Annual Income (USD)"
            name="annualIncome"
            rules={[{ required: true, message: "Please enter annual income" }]}
          >
            <Input type="number" placeholder="Enter annual income" min={1} />
          </Form.Item>

          <Form.Item
            label="Savings And Investments (USD)"
            name="savingAndInvestments"
            rules={[
              {
                required: true,
                message: "Please enter savings and investments",
              },
            ]}
          >
            <Input
              type="number"
              placeholder="Enter savings and investments"
              min={1}
            />
          </Form.Item>

          <Form.Item
            label="Number of Employees"
            name="noOfEmployees"
            rules={[
              { required: true, message: "Please enter number of employees" },
            ]}
          >
            <Input
              type="number"
              placeholder="Enter number of employees"
              min={1}
            />
          </Form.Item>

          <Form.Item
            label="Approximate Total Assets (USD)"
            name="approximateTotalAssets"
            rules={[
              {
                required: true,
                message: "Please enter approximate total assets",
              },
            ]}
          >
            <Input type="number" placeholder="Enter total assets" min={1} />
          </Form.Item>

          <Form.Item
            label="Source of Funds and Wealth"
            name="sourceOfWealth"
            rules={[
              {
                required: true,
                message: "Please select source of funds and wealth",
              },
            ]}
            className="md:col-span-2"
          >
            <Select
              mode="multiple"
              maxTagCount="responsive"
              className="onboarding-multi-select"
              placeholder="Select source of funds and wealth"
              options={CORPORATE_SOURCE_OF_WEALTH_OPTIONS}
            />
          </Form.Item>

          {Array.isArray(sourceOfWealth) &&
            sourceOfWealth.includes(CORPORATE_SOURCE_OF_WEALTH_OTHER) && (
              <Form.Item
                label="Please specify other source of funds and wealth"
                name="otherSourceOfWealth"
                rules={[
                  {
                    required: true,
                    message: "Please specify other source of funds and wealth",
                  },
                ]}
                className="md:col-span-2"
              >
                <Input placeholder="Enter other source of funds and wealth" />
              </Form.Item>
            )}
        </div>

        {isDisabled ? null : (
          <div className="flex justify-between mt-8">
            <Button onClick={onPrevious}>Previous</Button>
            <Button type="primary" htmlType="submit" loading={isSubmitLoading}>
              {isSubmitLoading ? "Submitting..." : "Submit"}
            </Button>
          </div>
        )}
      </Form>
      {isDisabled && (
        <div className="flex justify-between mt-8">
          <Button onClick={onPrevious}>Previous</Button>
          <Button type="primary" onClick={onNext}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default FinancialInfo;
